import { CreditCardInput } from '../../PaymentTypeSelection/types/creditCardInput'
import { BankAccountInput } from '../../PaymentTypeSelection/types/bankAccountInput'
import { reportToSentry } from 'utils/reportToSentry'
import { addMagentoEnvAuthHeaders } from '../../../../utils/magentoEnvAuthHeaders'
import { getAuthHeader } from '../../../../utils/auth/helpers'

async function submitRecurringCharge({
  paymentProfileId,
  amount,
  newCard,
  newBankAccount,
  patientHash,
  startDate,
  intervalLength,
  intervalUnit,
  totalOccurrences,
  invoiceNumber,
  name,
  orderDescription,
  initialPayment,
  customerToken
}: {
  paymentProfileId?: string;
  amount: number;
  newCard?: CreditCardInput;
  newBankAccount?: BankAccountInput;
  patientHash?: string;
  startDate: string;
  intervalLength: string;
  intervalUnit: string;
  totalOccurrences: string;
  invoiceNumber?: string;
  name?: string;
  orderDescription?: string;
  initialPayment?: boolean;
  customerToken?: string;
}) {
  if ( !patientHash && !customerToken )
    return {
      status: `error`,
      message: `Unauthorized.`
    }

  if (
    !paymentProfileId &&
    !newCard?.cardNumber &&
    !newBankAccount?.accountNumber
  )
    return {
      status: `error`,
      message: `No payment information provided.`
    }

  try {
    const response =
        await createRecurringCharge({
          amount,
          ...( newCard?.cardNumber && {
            cardNumber: newCard.cardNumber?.replaceAll( `-`, `` )
          }),
          ...( newCard?.expirationYear &&
            newCard.expirationMonth && {
            expirationDate: `${newCard.expirationYear}-${newCard.expirationMonth}`
          }),
          ...( newCard?.CVV && {
            cardCode: newCard.CVV
          }),
          ...( newCard?.nameOnCard && {
            nameOnAccount: newCard.nameOnCard
          }),
          ...( newBankAccount?.accountType && {
            accountType: newBankAccount?.accountType?.toLowerCase()
          }),
          ...( newBankAccount?.accountNumber && {
            accountNumber: newBankAccount.accountNumber
          }),
          ...( newBankAccount?.routingNumber && {
            routingNumber: newBankAccount.routingNumber
          }),
          ...( newBankAccount?.nameOnAccount && {
            nameOnAccount: newBankAccount.nameOnAccount
          }),
          ...( patientHash ? {
            patientHash
          } : {}),
          ...( customerToken
            ? {
              token: customerToken,
              ...addMagentoEnvAuthHeaders()
            }
            : {}),
          ...( name && {
            name
          }),
          ...( invoiceNumber && {
            invoiceNumber
          }),
          ...( orderDescription && {
            orderDescription
          }),
          ...( initialPayment && {
            initialPayment
          }),
          startDate,
          intervalLength,
          intervalUnit,
          totalOccurrences
        })

    if ( response?.messages?.resultCode?.toLowerCase() === `ok` && response?.messages?.message?.[0]?.text === `Successful.` )
      return {
        response: JSON.stringify( response ),
        status: `success`,
        message: `Subscription started successfully.`
      }

    reportToSentry( new Error( `Error submitting charge` ), {
      paymentProfileId,
      amount,
      newCard: JSON.stringify( newCard ),
      newBankAccount: JSON.stringify( newBankAccount ),
      patientHash,
      response: JSON.stringify( response )
    })

    return {
      status: `error`,
      message: `An exception occurred attempting to submit your subscription. Please try again.`
    }
  } catch ( e ) {
    reportToSentry( new Error( `Charge Failed` ), {
      paymentProfileId,
      amount,
      newCard: JSON.stringify( newCard ),
      newBankAccount: JSON.stringify( newBankAccount ),
      patientHash
    })
    return {
      status: `error`,
      message: `An exception occurred attempting to submit your subscription. Please try again.`
    }
  }
}

export const createRecurringCharge = async ( data: any ) => {
  const response = await fetch( `${process.env.REACT_APP_LAMBDA_AUTHNET_PAYMENTS_URL}/v2/recurring-charge`, {
    method: `POST`,
    headers: {
      'Content-Type': `application/json`,
      'Authorization': getAuthHeader(),
      ...addMagentoEnvAuthHeaders()
    },
    body: JSON.stringify( data )
  })

  return await response.json()
}

export default submitRecurringCharge
